import React, { useEffect, useRef, useState } from 'react';
import NumericInput from './numeric-input';
import { useWheelStep } from './use-wheel-step';

export interface CommitNumericFieldBaseProps {
  value: number | undefined;
  onCommit: (value: number) => void;
  /**
   * Called when the field is committed empty. Omit to make an empty field a
   * no-op — that is the difference between the required and optional variants.
   */
  onCommitEmpty?: () => void;
  /** How a numeric value is rendered into the input. */
  format: (value: number) => string;
  step?: number;
  min?: number;
  max?: number;
  placeholder?: string;
}

/**
 * Numeric input that commits on blur, Enter and unmount, and reverts on Escape.
 * Shared by CommitNumberField and CommitOptionalNumberField — they differ only
 * in how they format values and what an empty field means.
 */
const CommitNumericFieldBase: React.FC<CommitNumericFieldBaseProps> = ({
  value,
  onCommit,
  onCommitEmpty,
  format,
  step = 1,
  min,
  max,
  placeholder,
}) => {
  const initial = value === undefined || Number.isNaN(value) ? '' : format(value);
  const [draft, setDraft] = useState<string>(initial);
  const draftRef = useRef<string>(draft);
  const cancelledRef = useRef<boolean>(false);
  const commitRef = useRef<(next: string) => void>(() => {});
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  commitRef.current = (next: string) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    if (next === '') {
      onCommitEmpty?.();
      return;
    }
    const v = parseFloat(next);
    if (Number.isNaN(v)) return;
    if (v === value) return;
    onCommit(v);
  };

  useEffect(
    () => () => {
      commitRef.current(draftRef.current);
    },
    [],
  );

  const commit = () => commitRef.current(draftRef.current);

  const cancel = () => {
    cancelledRef.current = true;
    setDraft(initial);
    inputRef.current?.blur();
  };

  const parsedDraft = parseFloat(draft);
  const currentNumeric = Number.isNaN(parsedDraft) ? value : parsedDraft;
  useWheelStep(inputRef, currentNumeric, {
    step,
    min,
    max,
    onStep: (next) => setDraft(format(next)),
  });

  return (
    <NumericInput
      ref={inputRef}
      value={draft}
      step={step}
      min={min}
      max={max}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
    />
  );
};

export default CommitNumericFieldBase;
